import type { CheckboxChangeEventType, ModalDialogButtonSetting, RadioGroupResult } from '@myosh/odin-components'
import { Checkbox, Dialog, RadioGroup } from '@myosh/odin-components'
import type { FormEvent } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { GapAnalysisViewDto, TrainingMatrixViewDto } from '../../../API/rtk-query-api/store/tmsApi'
import Dropdown from '../dropdown/Dropdown'
import LoadingSpinner from '../loading-spinner'
import type { DropdownOption } from '../multi-select-dropdown/MultiSelectDropdown'
import SimpleInput from '../simple-input/SimpleInput'
import './save-view-modal.css'

const SaveOptions = {
  Create: 0,
  Save: 1
} as const
type SaveOptions = (typeof SaveOptions)[keyof typeof SaveOptions]

const saveOptionData = [
  { text: 'Create new view', value: SaveOptions.Create },
  { text: 'Save as existing view', value: SaveOptions.Save }
]

interface SaveViewModalProps {
  isModalVisible: boolean
  isLoading: boolean
  viewsToManage: Array<TrainingMatrixViewDto | GapAnalysisViewDto>
  onSaveView: (name: string, global: boolean) => void
  onUpdateView: (view: TrainingMatrixViewDto | GapAnalysisViewDto) => void
  onHideModal: () => void
  selectedViewId?: string
}

function SaveViewModal({
  isModalVisible,
  isLoading,
  viewsToManage,
  onSaveView,
  onUpdateView,
  onHideModal,
  selectedViewId
}: SaveViewModalProps) {
  const formId = 'save-view-modal'

  const isCurrentlyGlobalView = useRef(false)

  const [isGlobal, setIsGlobal] = useState(false)
  const [newViewName, setNewViewName] = useState('')
  const [saveOption, setSaveOption] = useState<SaveOptions>(SaveOptions.Create)
  const [selectedExistingView, setSelectedExistingView] = useState<DropdownOption | undefined>(undefined)

  const availableViews = useMemo(() => {
    return viewsToManage.map(view => ({
      text: view.title ?? '',
      value: view.id ?? ''
    }))
  }, [viewsToManage])

  const setCurrentView = () => {
    if (selectedViewId) {
      const selectedView = viewsToManage.find(view => view.id === selectedViewId)
      setNewViewName(selectedView?.title ?? '')
      setSelectedExistingView({ text: selectedView?.title ?? '', value: selectedView?.id ?? '' })
      setSaveOption(SaveOptions.Save)

      const isSelectedViewGlobal = selectedView?.global ?? false
      setIsGlobal(isSelectedViewGlobal)
      isCurrentlyGlobalView.current = isSelectedViewGlobal
    } else {
      setSaveOption(SaveOptions.Create)
      setSelectedExistingView(undefined)
    }
  }

  useEffect(() => {
    if (isModalVisible) {
      setCurrentView()
    }

    return () => {
      setNewViewName('')
      setSaveOption(SaveOptions.Create)
      setSelectedExistingView(undefined)
    }
  }, [isModalVisible, selectedViewId])

  useEffect(() => {
    if (saveOption === SaveOptions.Save && selectedExistingView) {
      const selectedView = viewsToManage.find(view => view.id === selectedExistingView.value)
      const isSelectedViewGlobal = selectedView?.global ?? false
      setIsGlobal(isSelectedViewGlobal)
      isCurrentlyGlobalView.current = isSelectedViewGlobal
    } else if (saveOption === SaveOptions.Create) {
      setIsGlobal(false)
      isCurrentlyGlobalView.current = false
    }
  }, [saveOption, selectedExistingView, viewsToManage])

  const handleViewSave = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()

      if (saveOption === SaveOptions.Save && selectedExistingView) {
        const selectedView = viewsToManage.find(view => view.id === selectedExistingView.value)
        if (selectedView) {
          const viewToUpdate = {
            ...selectedView,
            global: selectedView.global ? selectedView.global : isGlobal
          } satisfies TrainingMatrixViewDto | GapAnalysisViewDto

          onUpdateView(viewToUpdate)
        }
      } else {
        onSaveView(newViewName.trim(), isGlobal)
      }
    },
    [saveOption, newViewName, isGlobal, viewsToManage, selectedExistingView]
  )

  const handleSaveOptionChange = (result?: RadioGroupResult) => {
    if (result) {
      setSaveOption(result.value as SaveOptions)
      if (result.value === SaveOptions.Create) {
        setSelectedExistingView(undefined)
      }
    }
  }

  const handleExistingViewChange = (result?: DropdownOption) => {
    setSelectedExistingView(result)
  }

  const handleSelectGlobalView = (selection?: CheckboxChangeEventType) => setIsGlobal(selection?.checked ?? false)
  const handleChangeViewName = (value: string | number) => setNewViewName(String(value))

  const isSaveDisabled = useMemo(() => {
    if (isLoading) return true
    if (saveOption === SaveOptions.Create) {
      return newViewName.trim() === ''
    }
    if (saveOption === SaveOptions.Save) {
      return !selectedExistingView
    }
    return false
  }, [isLoading, saveOption, newViewName, selectedExistingView])

  const modalButtons = useMemo(() => {
    return [
      {
        type: 'primary',
        text: 'Save',
        name: 'save',
        form: formId,
        closeModalOnClick: false,
        disabled: isSaveDisabled
      }
    ] as Array<ModalDialogButtonSetting>
  }, [isSaveDisabled])

  const isGlobalCheckboxDisabled = useMemo(() => {
    if (saveOption === SaveOptions.Save && selectedExistingView) {
      return isGlobal && isCurrentlyGlobalView.current
    }
    return false
  }, [saveOption, selectedExistingView, isGlobal])

  return (
    <>
      {isModalVisible && (
        <Dialog
          header='View'
          className='save-view-modal'
          contentClassName='save-view-modal__content'
          visible={isModalVisible}
          buttons={modalButtons}
          buttonType='submit'
          onClose={onHideModal}>
          <form onSubmit={handleViewSave} id={formId} className='save-view-modal__form'>
            <RadioGroup
              name='saveOption'
              data={saveOptionData}
              textField='text'
              valueField='value'
              orientation='HORIZONTAL'
              value={{ text: '', value: saveOption }}
              onChange={handleSaveOptionChange}
            />
            {saveOption === SaveOptions.Save && (
              <Dropdown
                label=''
                placeholder='Select a view'
                options={availableViews}
                selectedOption={selectedExistingView}
                onChange={handleExistingViewChange}
                showClear={false}
                appendTo='self'
                fullWidth
              />
            )}
            {saveOption === SaveOptions.Create && (
              <SimpleInput label='Name' value={newViewName} onInput={handleChangeViewName} />
            )}
            <Checkbox
              key={`global-checkbox-${selectedExistingView?.value ?? 'new'}-${isGlobal}`}
              label='Global view (visible to everyone)'
              name='global-view-checkbox'
              initialChecked={isGlobal}
              onChange={handleSelectGlobalView}
              disabled={isGlobalCheckboxDisabled}
            />
            {isLoading && <LoadingSpinner />}
          </form>
        </Dialog>
      )}
    </>
  )
}

export default SaveViewModal
